import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { YachtLogo } from '../components/YachtLogo';
import { Zap, ShieldCheck, Box, Activity, Layers, Rocket, ArrowRight, Github, Mail } from 'lucide-react';

import Navbar from '../components/Navbar';

export default function Product() {
  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="pt-48 pb-32 px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl md:text-8xl font-mono uppercase tracking-tighter leading-[0.9] mb-8">
                The Product <br/>
                <span className="text-[#0074D9]">Synthesis Engine.</span>
              </h1>
              <p className="text-zinc-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
                A new category of builder. Not a CMS, not an IDE—an autonomous synthesis engine that translates creative intent into technical existence.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Core Pillars */}
        <section className="py-32 px-8 bg-zinc-950 border-y border-white/5">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-6">
              <div className="w-12 h-12 bg-[#001F3F] border border-[#0074D9]/30 rounded-2xl flex items-center justify-center text-[#0074D9]">
                <Box size={24} />
              </div>
              <h3 className="text-xl font-mono uppercase tracking-tight">The Assembler</h3>
              <p className="text-zinc-500 font-light leading-relaxed">
                Yacht's core engine doesn't just generate text; it synthesizes production-grade architecture. It understands data models, routing, and component state as a unified whole.
              </p>
            </div>
            <div className="space-y-6">
              <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center text-white">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-xl font-mono uppercase tracking-tight">Refined Integrity</h3>
              <p className="text-zinc-500 font-light leading-relaxed">
                Zero vendor lock-in. Every build produces clean, readable TypeScript and Tailwind CSS. Export your source code to any environment with zero friction.
              </p>
            </div>
            <div className="space-y-6">
              <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center text-white">
                <Zap size={24} />
              </div>
              <h3 className="text-xl font-mono uppercase tracking-tight">Zero-Ops Hosting</h3>
              <p className="text-zinc-500 font-light leading-relaxed">
                Deployments happen on the global edge. No managing servers, no configuring DNS. Yacht handles the distribution so you can focus on the product.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Walkthrough */}
        <section className="py-32 px-8">
          <div className="max-w-6xl mx-auto space-y-32">
            <div className="flex flex-col md:flex-row items-center gap-24">
              <div className="flex-1 space-y-8">
                <h2 className="tech-label">Think Stage</h2>
                <h3 className="text-4xl md:text-5xl font-mono uppercase tracking-tighter leading-tight">From Raw Research to <span className="text-[#0074D9]">Technical Briefs.</span></h3>
                <p className="text-zinc-400 text-lg font-light">
                  Paste your raw research, founder notes, or competitor teardowns. Our AI Accelerator synthesizes this into a high-fidelity design brief that defines your MVP's core logic.
                </p>
                <div className="flex items-center gap-4 pt-4">
                  <div className="px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-400">Competitive Analysis</div>
                  <div className="px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-400">User Flow Mapping</div>
                </div>
              </div>
              <div className="flex-1 aspect-square bg-zinc-900/30 border border-zinc-800 rounded-[48px] overflow-hidden p-12">
                <div className="w-full h-full bg-black border border-zinc-800 rounded-3xl p-8 space-y-6">
                  <div className="h-4 w-1/2 bg-[#0074D9]/20 rounded-full animate-pulse" />
                  <div className="space-y-3">
                    <div className="h-2 w-full bg-zinc-800 rounded-full" />
                    <div className="h-2 w-full bg-zinc-800 rounded-full" />
                    <div className="h-2 w-3/4 bg-zinc-800 rounded-full" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-8">
                    <div className="h-24 bg-zinc-900 rounded-xl border border-zinc-800" />
                    <div className="h-24 bg-zinc-900 rounded-xl border border-zinc-800" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse items-center gap-24">
              <div className="flex-1 space-y-8 text-right md:text-left">
                <h2 className="tech-label md:text-left">Build Stage</h2>
                <h3 className="text-4xl md:text-5xl font-mono uppercase tracking-tighter leading-tight">Visual Assembly with <span className="text-[#0074D9]">Production Power.</span></h3>
                <p className="text-zinc-400 text-lg font-light leading-relaxed">
                  Use the Assembler to generate full applications in seconds. Refine layouts visually while the engine manages the underlying TypeScript architecture in real-time.
                </p>
                <Link to="/studio" className="inline-flex items-center gap-3 text-[#0074D9] font-mono uppercase text-xs tracking-widest hover:gap-5 transition-all">
                  Explore the Assembler <ArrowRight size={14} />
                </Link>
              </div>
              <div className="flex-1 aspect-[4/3] bg-[#0074D9]/10 border border-[#0074D9]/20 rounded-[48px] overflow-hidden p-8">
                <div className="w-full h-full bg-[#050505] border border-zinc-800 rounded-3xl shadow-2xl flex flex-col">
                  <div className="h-10 border-b border-zinc-900 px-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#FF4136]" />
                    <div className="w-2 h-2 rounded-full bg-[#FF851B]" />
                    <div className="w-2 h-2 rounded-full bg-[#2ECC40]" />
                  </div>
                  <div className="flex-1 p-6 grid grid-cols-3 gap-4">
                    <div className="space-y-4">
                      <div className="h-32 bg-zinc-900/50 rounded-xl border border-zinc-800" />
                      <div className="h-12 bg-zinc-900/50 rounded-xl border border-zinc-800" />
                    </div>
                    <div className="col-span-2 bg-[#001F3F]/20 rounded-xl border border-[#0074D9]/20" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Global Distribution */}
        <section className="py-32 px-8 bg-zinc-950">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="tech-label">Kinetics Engine</h2>
            <h3 className="text-5xl font-mono uppercase tracking-tighter text-white">Ship Everywhere. <br/> <span className="text-[#0074D9]">Instantly.</span></h3>
            <p className="text-zinc-500 text-lg font-light leading-relaxed">
              Yacht Labs provides a global distribution engine. Deploy your synthesized apps to custom domains, generate Product Hunt kits, and manage your mission control from a single dashboard.
            </p>
          </div>
        </section>
      </main>

      <footer className="p-24 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-24">
          <div className="col-span-2 space-y-8">
            <Link to="/" className="flex items-center gap-4">
              <YachtLogo className="w-8 h-8" />
              <span className="font-mono text-2xl uppercase tracking-widest">Yacht Labs</span>
            </Link>
            <p className="text-zinc-500 font-light leading-relaxed max-w-sm">
              Synthesizing the future of digital architecture. Empowering founders to bridge the gap between intent and existence.
            </p>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Platform</h4>
            <div className="flex flex-col gap-4">
              <Link to="/product" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Synthesis</Link>
              <Link to="/use-cases" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Use Cases</Link>
              <Link to="/solutions" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Solutions</Link>
              <Link to="/pricing" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Pricing</Link>
            </div>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Connect</h4>
            <div className="flex flex-col gap-4">
              <a href="#" className="flex items-center gap-3 text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">
                <Github size={14} /> GitHub
              </a>
              <a href="mailto:hello@yacht-labs.com" className="flex items-center gap-3 text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">
                <Mail size={14} /> Contact
              </a>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-24 pt-8 border-t border-zinc-900 flex justify-between items-center text-[10px] font-mono uppercase tracking-widest text-zinc-700">
          <span>© 2026 Yacht Labs Inc. / All Rights Reserved.</span>
          <span>Latency: 24ms / Status: Nominal</span>
        </div>
      </footer>
    </div>
  );
}
